import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';

const SAV_TYPOS = ['SAV', 'SAV24h', 'SAV48h', 'SAV96h', 'pré_visite', 'Pré-visite'];

function stockColor(s) {
  if (s === null || s === undefined) return { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db' };
  if (s <= 0)  return { bg: '#fee2e2', color: '#b91c1c', border: '#fecaca' };
  if (s <= 5)  return { bg: '#fef3c7', color: '#b45309', border: '#fde68a' };
  return { bg: '#dcfce7', color: '#15803d', border: '#86efac' };
}

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('fr-FR'); } catch { return d; }
}

function typeBadge(type) {
  const cfg = {
    reapprovisionnement: { label: '↓ Réappro',        bg: '#dcfce7', color: '#15803d' },
    pose_cri:            { label: '🔧 Pose CRI',       bg: '#fee2e2', color: '#b91c1c' },
    correction_plus:     { label: '✏️ Correction +',  bg: '#eff6ff', color: '#1d4ed8' },
    correction_moins:    { label: '✏️ Correction −',  bg: '#fef3c7', color: '#b45309' },
    reception:           { label: '📦 Réception',      bg: '#dcfce7', color: '#15803d' },
    sortie:              { label: '↑ Sortie',          bg: '#fee2e2', color: '#b91c1c' },
  };
  const c = cfg[type] || { label: type, bg: '#f3f4f6', color: '#374151' };
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: c.bg, color: c.color }}>
      {c.label}
    </span>
  );
}

// ── Modal ────────────────────────────────────────────────────────────────────
function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={onClose}>
      <div style={{ background: 'var(--bg)', borderRadius: 14, boxShadow: '0 8px 40px rgba(0,0,0,.25)', width: '100%', maxWidth: 560, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <span style={{ fontWeight: 800, fontSize: 15 }}>{title}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text3)', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ overflowY: 'auto', padding: '16px 20px', flex: 1 }}>{children}</div>
      </div>
    </div>
  );
}

// ── Champ numérique pour les modals ─────────────────────────────────────────
function NumInput({ value, onChange, min, label }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 4 }}>{label}</label>
      <input type="number" min={min ?? 1} value={value}
        onChange={e => onChange(Number(e.target.value))}
        style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 14, boxSizing: 'border-box' }} />
    </div>
  );
}

function TextInput({ value, onChange, label, placeholder }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 4 }}>{label}</label>
      <input type="text" value={value} placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 14, boxSizing: 'border-box' }} />
    </div>
  );
}

// ── Carte technicien ─────────────────────────────────────────────────────────
function TechCard({ techId, data, onReappro, onCorrection, onHistory }) {
  const stock = data.stock_base ?? 0;
  const sc = stockColor(stock);
  return (
    <div style={{ background: 'var(--bg)', border: `1px solid ${sc.border}`, borderRadius: 12, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 13 }}>{data.technicien_nom || techId}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{techId}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 28, fontWeight: 900, color: sc.color, lineHeight: 1 }}>{stock}</div>
          <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>ONT</div>
        </div>
      </div>
      <div style={{ height: 6, borderRadius: 4, background: 'var(--bg3)', overflow: 'hidden' }}>
        <div style={{ height: '100%', borderRadius: 4, background: sc.color, width: `${Math.min(100, (stock / 50) * 100)}%`, transition: 'width .3s' }} />
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button onClick={() => onReappro(techId, data)} style={{ flex: 1, fontSize: 11, fontWeight: 700, padding: '5px 8px', borderRadius: 8, border: '1px solid #86efac', background: '#dcfce7', color: '#15803d', cursor: 'pointer' }}>
          ↓ Réappro
        </button>
        <button onClick={() => onCorrection(techId, data)} style={{ flex: 1, fontSize: 11, fontWeight: 700, padding: '5px 8px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text2)', cursor: 'pointer' }}>
          ✏️ Corriger
        </button>
        <button onClick={() => onHistory(techId, data)} style={{ flex: 1, fontSize: 11, fontWeight: 700, padding: '5px 8px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text2)', cursor: 'pointer' }}>
          📋 Historique
        </button>
      </div>
    </div>
  );
}

// ── Page principale ──────────────────────────────────────────────────────────
export default function StockONT() {
  const [stock, setStock]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  // Modals
  const [modalReappro, setModalReappro]         = useState(null); // { techId, data }
  const [modalCorrection, setModalCorrection]   = useState(null); // { techId, data }
  const [modalHistory, setModalHistory]         = useState(null); // { techId, data, isDepot }
  const [modalDepotReception, setModalDepotReception] = useState(false);

  // Form states
  const [fmtQty, setFmtQty]     = useState(1);
  const [fmtNote, setFmtNote]   = useState('');
  const [fmtDelta, setFmtDelta] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/stock-ont');
      setStock(res.data);
    } catch (e) {
      setError('Erreur chargement stock');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doAction = async (fn) => {
    setSaving(true);
    setError('');
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const submitDepotReception = () => doAction(async () => {
    await api.post('/admin/stock-ont/depot/reception', { quantite: fmtQty, note: fmtNote });
    setModalDepotReception(false);
  });

  const submitReappro = () => doAction(async () => {
    await api.post('/admin/stock-ont/reappro', { tech_id: modalReappro.techId, quantite: fmtQty, note: fmtNote });
    setModalReappro(null);
  });

  const submitCorrection = () => doAction(async () => {
    await api.post(`/admin/stock-ont/${modalCorrection.techId}/correction`, { delta: fmtDelta, note: fmtNote });
    setModalCorrection(null);
  });

  const openReappro = (techId, data) => { setFmtQty(1); setFmtNote(''); setModalReappro({ techId, data }); };
  const openCorrection = (techId, data) => { setFmtDelta(0); setFmtNote(''); setModalCorrection({ techId, data }); };
  const openHistory = (techId, data, isDepot = false) => setModalHistory({ techId, data, isDepot });
  const openDepotReception = () => { setFmtQty(1); setFmtNote(''); setModalDepotReception(true); };

  if (loading) return <div style={{ padding: 32, color: 'var(--text3)' }}>Chargement…</div>;

  const { depot, ...techs } = stock || {};
  const techEntries = Object.entries(techs).sort((a, b) => a[1].technicien_nom?.localeCompare(b[1].technicien_nom));
  const totalTech = techEntries.reduce((s, [, d]) => s + (d.stock_base || 0), 0);
  const totalAll = totalTech + (depot?.stock || 0);

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>Stock ONT</h2>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 3 }}>
            Total en circulation : <strong>{totalAll}</strong> ONT ({totalTech} chez les techniciens · {depot?.stock ?? 0} en dépôt)
          </div>
        </div>
        <button onClick={load} style={{ fontSize: 12, padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text2)', cursor: 'pointer', fontWeight: 600 }}>
          ↻ Actualiser
        </button>
      </div>

      {error && <div style={{ padding: '10px 14px', borderRadius: 8, background: '#fee2e2', color: '#b91c1c', marginBottom: 16, fontSize: 13 }}>{error}</div>}

      {/* ── Dépôt ── */}
      <div style={{ background: 'var(--bg)', border: '2px solid #6366f1', borderRadius: 14, padding: '18px 20px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 40, fontWeight: 900, color: '#4f46e5', lineHeight: 1 }}>{depot?.stock ?? 0}</div>
        <div style={{ flex: 1, minWidth: 120 }}>
          <div style={{ fontWeight: 800, fontSize: 15 }}>🏭 Dépôt VALEA</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>ONT disponibles au dépôt</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={openDepotReception} style={{ fontSize: 12, fontWeight: 700, padding: '8px 14px', borderRadius: 8, border: '1px solid #a5b4fc', background: '#ede9fe', color: '#4f46e5', cursor: 'pointer' }}>
            📦 Réception dépôt
          </button>
          <button onClick={() => openHistory('depot', depot, true)} style={{ fontSize: 12, fontWeight: 700, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text2)', cursor: 'pointer' }}>
            📋 Historique
          </button>
        </div>
      </div>

      {/* ── Grille techniciens ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
        {techEntries.map(([techId, data]) => (
          <TechCard key={techId} techId={techId} data={data}
            onReappro={openReappro}
            onCorrection={openCorrection}
            onHistory={(id, d) => openHistory(id, d, false)}
          />
        ))}
      </div>

      {/* ── Modal : Réception dépôt ── */}
      {modalDepotReception && (
        <Modal title="📦 Réception ONT au dépôt" onClose={() => setModalDepotReception(false)}>
          <NumInput label="Quantité reçue" value={fmtQty} onChange={setFmtQty} min={1} />
          <TextInput label="Note (optionnel)" value={fmtNote} onChange={setFmtNote} placeholder="Ex: livraison fournisseur" />
          <button onClick={submitDepotReception} disabled={saving || fmtQty < 1} style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: '#4f46e5', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14, marginTop: 4 }}>
            {saving ? '…' : `Ajouter ${fmtQty} ONT au dépôt`}
          </button>
        </Modal>
      )}

      {/* ── Modal : Réappro tech ── */}
      {modalReappro && (
        <Modal title={`↓ Réappro — ${modalReappro.data.technicien_nom || modalReappro.techId}`} onClose={() => setModalReappro(null)}>
          <div style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--bg2)', marginBottom: 14, fontSize: 13 }}>
            Stock dépôt disponible : <strong>{depot?.stock ?? 0} ONT</strong>
          </div>
          <NumInput label="Quantité à transférer" value={fmtQty} onChange={setFmtQty} min={1} />
          <TextInput label="Note (optionnel)" value={fmtNote} onChange={setFmtNote} placeholder="Ex: Camion lundi" />
          <button onClick={submitReappro} disabled={saving || fmtQty < 1 || fmtQty > (depot?.stock ?? 0)} style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: '#15803d', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14, marginTop: 4 }}>
            {saving ? '…' : `Transférer ${fmtQty} ONT → ${modalReappro.data.technicien_nom?.split(' ')[0] || modalReappro.techId}`}
          </button>
        </Modal>
      )}

      {/* ── Modal : Correction manuelle ── */}
      {modalCorrection && (
        <Modal title={`✏️ Correction — ${modalCorrection.data.technicien_nom || modalCorrection.techId}`} onClose={() => setModalCorrection(null)}>
          <div style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--bg2)', marginBottom: 14, fontSize: 13 }}>
            Stock actuel : <strong>{modalCorrection.data.stock_base ?? 0} ONT</strong>
          </div>
          <NumInput label="Variation (négatif pour retrait)" value={fmtDelta} onChange={setFmtDelta} min={-999} />
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12 }}>
            Nouveau stock : <strong>{Math.max(0, (modalCorrection.data.stock_base ?? 0) + fmtDelta)}</strong>
          </div>
          <TextInput label="Motif (obligatoire)" value={fmtNote} onChange={setFmtNote} placeholder="Ex: Inventaire physique" />
          <button onClick={submitCorrection} disabled={saving || !fmtNote.trim()} style={{ width: '100%', padding: '10px', borderRadius: 8, border: 'none', background: '#1d4ed8', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14, marginTop: 4 }}>
            {saving ? '…' : 'Appliquer la correction'}
          </button>
        </Modal>
      )}

      {/* ── Modal : Historique ── */}
      {modalHistory && (
        <Modal
          title={modalHistory.isDepot ? '📋 Historique dépôt' : `📋 Historique — ${modalHistory.data.technicien_nom || modalHistory.techId}`}
          onClose={() => setModalHistory(null)}
        >
          {(modalHistory.data.historique || []).length === 0
            ? <div style={{ color: 'var(--text3)', fontSize: 13 }}>Aucun mouvement enregistré.</div>
            : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border)' }}>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--text3)', fontWeight: 600 }}>Date</th>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--text3)', fontWeight: 600 }}>Type</th>
                    <th style={{ textAlign: 'right', padding: '4px 8px', color: 'var(--text3)', fontWeight: 600 }}>Qté</th>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--text3)', fontWeight: 600 }}>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {(modalHistory.data.historique || []).map((h, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{fmtDate(h.date)}</td>
                      <td style={{ padding: '6px 8px' }}>{typeBadge(h.type)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700,
                        color: ['reapprovisionnement','reception','correction_plus'].includes(h.type) ? '#15803d' : '#b91c1c' }}>
                        {['reapprovisionnement','reception','correction_plus'].includes(h.type) ? '+' : '−'}{h.quantite}
                      </td>
                      <td style={{ padding: '6px 8px', color: 'var(--text2)' }}>{h.note || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          }
        </Modal>
      )}
    </div>
  );
}
